import { GuestModel } from 'api-hooks/guest';
import { Value } from 'baseui/select';
import { generateId } from 'common/utils/string';
import { useFormState } from 'components/elements/form';
import deleteColumn from 'components/elements/table/editable-columns/delete-column';
import TableInput from 'components/elements/table/table-input';
import {
  ActionType,
  ColumnDataType,
  IColumn,
} from 'components/elements/table/type';
import useGetGuestColumn from 'hooks/use-get-guest-column';
import { guestPreferencesTransformer } from 'legacy-modules/sales/ticket-sale/hooks/use-get-default-values';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { addNewDetails, addNewGuest } from '../../hooks/use-get-default-values';
import {
  CruisePurchaseGuestItemFormType,
  CruisePurchaseFormType,
} from '../form-type';

interface Props {
  parentIndex: number;
}

function useGetCruiseGuestTableList(): IColumn<CruisePurchaseGuestItemFormType>[] {
  const { t } = useTranslation();
  const { editable } = useFormState();
  const guestColumn = useGetGuestColumn();

  return [
    {
      header: t('common:guest'),
      accessorKey: 'guestId',
      minSize: 250,
      dataType: ColumnDataType.select,
      required: true,
      selectProps: guestColumn,
    },
    ...deleteColumn(editable),
  ];
}

export default function CruiseGuestTable(props: Props) {
  const { parentIndex = 0 } = props;
  const { editable } = useFormState();
  const name = `cruisePurchases.${parentIndex}.cruisePurchaseGuests` as const;
  const detailsName =
    `cruisePurchases.${parentIndex}.cruisePurchaseDetails` as const;
  const { control, trigger, getValues, clearErrors, setValue } =
    useFormContext<CruisePurchaseFormType>();

  const { append, remove, update } = useFieldArray({
    name,
    control,
    keyName: '__customId',
  });

  const columns = useGetCruiseGuestTableList();

  const dispatch = React.useCallback(
    (action) => {
      const columnId = action.columnId as keyof CruisePurchaseGuestItemFormType;
      const type = action.type as ActionType;
      const value = action.value;
      const extra = action.extra as Value;
      const currentRowIndex = action.rowIndex as number;
      const guests = getValues(name);
      const guest = guests[currentRowIndex];
      const details = getValues(detailsName);
      const isGroupedPrice = getValues(
        `cruisePurchases.${parentIndex}.isGroupedPrice`,
      );

      const otherVal: () => Partial<CruisePurchaseGuestItemFormType> = () => {
        if (columnId === 'guestId') {
          const _guest = extra?.[0]?.extra as GuestModel;
          const guestPreferences = guestPreferencesTransformer(_guest);
          return {
            guestName: extra?.[0]?.label as string,
            guestPreferences,
          };
        }
        return {};
      };

      if (type === 'addRow') {
        append(addNewGuest());

        if (!isGroupedPrice) {
          setValue(detailsName, [...details, addNewDetails()]);
        }
        trigger(name);
      } else if (type === 'removeRow') {
        remove(currentRowIndex);

        if (!isGroupedPrice) {
          setValue(
            detailsName,
            details.filter((_, idx) => idx !== currentRowIndex),
          );
        }
        trigger(name);
      } else if (type === 'update_cell') {
        update(currentRowIndex, {
          ...guest,
          [columnId]: value,
          guestDetailId: guest.guestDetailId || generateId(),
          ...otherVal(),
        });
        details.forEach((row, index) => {
          if (row.guestDetailId === guest.guestDetailId) {
            setValue(`${detailsName}.${index}`, {
              ...row,
              guestId: getValues(name)[currentRowIndex].guestId,
              guestDetailId: getValues(name)[currentRowIndex].guestDetailId,
              ...otherVal(),
            });
          }
        });
        clearErrors(name);
      }
    },
    [
      append,
      clearErrors,
      detailsName,
      getValues,
      name,
      parentIndex,
      remove,
      setValue,
      trigger,
      update,
    ],
  );

  return (
    <div style={{ width: '100%' }}>
      <TableInput
        columns={columns}
        data={getValues(name)}
        dispatch={dispatch}
        disabled={!editable}
        name={name}
        uniqueRowKey="customId"
      />
    </div>
  );
}
